import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';

export type ActiveFloatingPanel = 'ask' | 'notes' | null;

export type OpenFloatingPanelType = Exclude<ActiveFloatingPanel, null>;

type ActionPopoverContextType = {
  anchorEl: HTMLElement | null;
  activePanel: ActiveFloatingPanel;
  popoverAnchorRef: React.RefObject<HTMLDivElement | null>;
  popoverContainerEl: HTMLElement | null;
  isAskPopoverOpen: boolean;
  isNotesPopoverOpen: boolean;
  isAnyPanelOpen: boolean;
  isFocusedModalOpen: boolean;
  // True while the inline Document Upload view is open on the Patient Summary (which has no
  // dedicated upload URL on PSO). Lets the floating-actions host show/lift the FAB there.
  isDocumentUploadOpen: boolean;
  openFloatingPanel: (panel: OpenFloatingPanelType, anchor?: HTMLElement | null) => void;
  closeFloatingPanel: () => void;
  setPopoverContainerEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  setIsFocusedModalOpen: (behind: boolean) => void;
  setIsDocumentUploadOpen: (open: boolean) => void;
};

const ActionPopoverContext = createContext<ActionPopoverContextType>({
  anchorEl: null,
  activePanel: null,
  popoverAnchorRef: { current: null },
  popoverContainerEl: null,
  isAskPopoverOpen: false,
  isNotesPopoverOpen: false,
  isAnyPanelOpen: false,
  isFocusedModalOpen: false,
  isDocumentUploadOpen: false,
  openFloatingPanel: () => {},
  closeFloatingPanel: () => {},
  setPopoverContainerEl: () => {},
  setIsFocusedModalOpen: () => {},
  setIsDocumentUploadOpen: () => {},
});

export const useActionPopoverContext = () => {
  return useContext(ActionPopoverContext);
};

export const ActionPopoverContextProvider = ({ children }: { children: ReactNode }) => {
  const [isFocusedModalOpen, setIsFocusedModalOpen] = useState(false);
  const [isDocumentUploadOpen, setIsDocumentUploadOpen] = useState(false);
  const [activePanel, setActivePanel] = useState<ActiveFloatingPanel>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverContainerEl, setPopoverContainerEl] = useState<HTMLElement | null>(null);
  const popoverAnchorRef = useRef<HTMLDivElement | null>(null);

  const openFloatingPanel = useCallback((panel: OpenFloatingPanelType, el?: HTMLElement | null) => {
    const target = el ?? popoverAnchorRef.current;
    if (!target) return;
    setActivePanel(panel);
    setAnchorEl(target);
  }, []);

  const closeFloatingPanel = useCallback(() => {
    setActivePanel(null);
    setAnchorEl(null);
  }, []);

  const isAskPopoverOpen = activePanel === 'ask';
  const isNotesPopoverOpen = activePanel === 'notes';
  const isAnyPanelOpen = activePanel !== null;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Allow global close requests from outside this provider.
    const handleForceClose = () => {
      closeFloatingPanel();
    };
    window.addEventListener('ask-popover:force-close', handleForceClose);
    return () => {
      window.removeEventListener('ask-popover:force-close', handleForceClose);
    };
  }, [closeFloatingPanel]);

  const value = useMemo(
    () => ({
      anchorEl,
      activePanel,
      popoverAnchorRef,
      isAskPopoverOpen,
      isNotesPopoverOpen,
      isAnyPanelOpen,
      openFloatingPanel,
      closeFloatingPanel,
      popoverContainerEl,
      setPopoverContainerEl,
      isFocusedModalOpen,
      setIsFocusedModalOpen,
      isDocumentUploadOpen,
      setIsDocumentUploadOpen,
    }),
    [
      activePanel,
      anchorEl,
      closeFloatingPanel,
      isAskPopoverOpen,
      isNotesPopoverOpen,
      isAnyPanelOpen,
      isFocusedModalOpen,
      isDocumentUploadOpen,
      openFloatingPanel,
      popoverContainerEl,
    ],
  );

  return <ActionPopoverContext.Provider value={value}>{children}</ActionPopoverContext.Provider>;
};

/**
 * When a Dialog opens while a floating panel is open, Material-UI's default behavior tries to
 * trap focus inside the Dialog, which can steal focus from the popover.
 * This hook provides dialog props that prevent focus conflicts while keeping both components functional.
 */
export const useActionPopoverControl = ({
  open,
  enableActionPopover = true,
}: {
  open: boolean;
  enableActionPopover?: boolean;
}) => {
  const { isAnyPanelOpen, setIsFocusedModalOpen } = useActionPopoverContext();

  useEffect(() => {
    // When enableActionPopover is false (or not provided), modal should sit over popover
    // so the floating panel becomes non-interactive. When true, allow it to remain interactive.
    if (!enableActionPopover) {
      setIsFocusedModalOpen(open);
    }
    return () => {
      if (!enableActionPopover) {
        setIsFocusedModalOpen(false);
      }
    };
  }, [open, enableActionPopover, setIsFocusedModalOpen]);

  const dialogProps = useMemo(() => {
    if (!enableActionPopover) {
      // No coordination needed - return empty props
      return {};
    }
    return { disableEnforceFocus: isAnyPanelOpen };
  }, [enableActionPopover, isAnyPanelOpen]);

  return { dialogProps };
};
